import { requestAssetApi } from './getAssetById';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return null;
}

function parseEpochMs(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const asNum = Number(value.trim());
    if (Number.isFinite(asNum)) {
      return asNum < 1e12 ? Math.round(asNum * 1000) : Math.round(asNum);
    }
    const parsed = Date.parse(value.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

export type MessagingMessage = {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  createdAt: number;
};

export type MessagingConversation = {
  id: string;
  peerUserId: string;
  peerLabel: string | null;
  peerAvatarSrc: string | null;
  lastMessage: MessagingMessage | null;
  unreadCount: number;
  updatedAt: number;
  createdAt: number;
};

function unwrapList(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (!isRecord(raw)) {
    return [];
  }
  for (const key of ['conversations', 'messages', 'items', 'data', 'results', 'rows'] as const) {
    const nested = raw[key];
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  return [];
}

export function parseMessagingMessage(raw: unknown): MessagingMessage | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = pickString(raw.id, raw.messageId, raw.message_id);
  const conversationId = pickString(
    raw.conversationId,
    raw.conversation_id,
    raw.threadId,
    raw.thread_id,
  );
  const senderId = pickString(raw.senderId, raw.sender_id, raw.fromUserId, raw.from_user_id);
  const text = typeof raw.text === 'string' ? raw.text : pickString(raw.body, raw.content) ?? '';
  if (!id || !conversationId || !senderId) {
    return null;
  }
  return {
    id,
    conversationId,
    senderId,
    text,
    createdAt: parseEpochMs(raw.createdAt ?? raw.created_at),
  };
}

export function parseMessagingConversation(raw: unknown): MessagingConversation | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = pickString(raw.id, raw.conversationId, raw.conversation_id);
  const peerUserId = pickString(
    raw.peerUserId,
    raw.peer_user_id,
    raw.otherUserId,
    raw.other_user_id,
  );
  if (!id || !peerUserId) {
    return null;
  }
  const lastRaw = raw.lastMessage ?? raw.last_message;
  const lastMessage =
    lastRaw == null ? null : parseMessagingMessage(lastRaw) ?? null;
  const unreadRaw = raw.unreadCount ?? raw.unread_count ?? 0;
  const unreadCount =
    typeof unreadRaw === 'number' && Number.isFinite(unreadRaw)
      ? Math.max(0, Math.round(unreadRaw))
      : Number(unreadRaw) || 0;
  const peerLabel = pickString(
    raw.peerLabel,
    raw.peer_label,
    raw.peerName,
    raw.peer_name,
    raw.peerDisplayName,
    raw.peer_display_name,
    raw.otherUserName,
    raw.other_user_name,
  );
  const peerAvatarSrc = pickString(
    raw.peerAvatarSrc,
    raw.peer_avatar_src,
    raw.peerAvatarUrl,
    raw.peer_avatar_url,
    raw.peerAvatar,
    raw.peer_avatar,
    raw.otherUserAvatar,
    raw.other_user_avatar,
  );
  return {
    id,
    peerUserId,
    peerLabel,
    peerAvatarSrc,
    lastMessage,
    unreadCount,
    updatedAt: parseEpochMs(raw.updatedAt ?? raw.updated_at),
    createdAt: parseEpochMs(raw.createdAt ?? raw.created_at),
  };
}

export function parseConversationsList(raw: unknown): MessagingConversation[] {
  const out: MessagingConversation[] = [];
  const seen = new Set<string>();
  for (const row of unwrapList(raw)) {
    const parsed = parseMessagingConversation(row);
    if (!parsed || seen.has(parsed.id)) {
      continue;
    }
    seen.add(parsed.id);
    out.push(parsed);
  }
  return out.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function parseMessagesList(raw: unknown): MessagingMessage[] {
  const out: MessagingMessage[] = [];
  const seen = new Set<string>();
  for (const row of unwrapList(raw)) {
    const parsed = parseMessagingMessage(row);
    if (!parsed || seen.has(parsed.id)) {
      continue;
    }
    seen.add(parsed.id);
    out.push(parsed);
  }
  return out.sort((a, b) => a.createdAt - b.createdAt);
}

/** POST `/conversations` — start or get 1:1 thread. */
export async function createOrGetConversation(peerUserId: string): Promise<MessagingConversation> {
  const raw = await requestAssetApi({
    method: 'POST',
    path: '/conversations',
    body: { peerUserId: peerUserId.trim() },
  });
  const parsed = parseMessagingConversation(raw);
  if (!parsed) {
    throw new Error('messaging-invalid-conversation');
  }
  return parsed;
}

/** GET `/conversations` */
export async function fetchConversations(): Promise<MessagingConversation[]> {
  const raw = await requestAssetApi({ method: 'GET', path: '/conversations' });
  return parseConversationsList(raw);
}

/** GET `/conversations/:id/messages` */
export async function fetchConversationMessages(
  conversationId: string,
  options?: { before?: string; limit?: number },
): Promise<MessagingMessage[]> {
  const params = new URLSearchParams();
  if (options?.before?.trim()) {
    params.set('before', options.before.trim());
  }
  if (options?.limit != null && Number.isFinite(options.limit)) {
    params.set('limit', String(Math.max(1, Math.round(options.limit))));
  }
  const qs = params.toString();
  const path = `/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ''}`;
  const raw = await requestAssetApi({ method: 'GET', path });
  return parseMessagesList(raw);
}

/** POST `/conversations/:id/messages` */
export async function sendConversationMessage(
  conversationId: string,
  text: string,
): Promise<MessagingMessage> {
  const raw = await requestAssetApi({
    method: 'POST',
    path: `/conversations/${encodeURIComponent(conversationId)}/messages`,
    body: { text },
  });
  const parsed = parseMessagingMessage(raw);
  if (!parsed) {
    throw new Error('messaging-invalid-message');
  }
  return parsed;
}

/** POST `/conversations/:id/read` */
export async function markConversationRead(conversationId: string): Promise<void> {
  await requestAssetApi({
    method: 'POST',
    path: `/conversations/${encodeURIComponent(conversationId)}/read`,
    body: {},
  });
}
